import { useState } from 'react';
import '../styles/FAQ.css';

const faqs = [
    {
        question: 'What is CensusZen?',
        answer: (
            <p>
                CensusZen is an AI-powered clinical assessment tool built for post-acute care
                facilities, including Inpatient Rehabilitation (IPR) and Long-Term Acute Care
                (LTACH) hospitals. Upload medical referral documents and receive structured
                clinical assessments and HPI narratives in minutes — replacing the 30–45 minute
                manual chart review process.
            </p>
        ),
    },
    {
        question: 'How does CensusZen work?',
        answer: (
            <>
                <p>CensusZen follows a three-step workflow:</p>
                <ol className="faq-detail-list">
                    <li>
                        <strong>Upload</strong> — drag and drop PDF or DOCX referral documents
                        into the app. Files are uploaded directly to secure cloud storage.
                    </li>
                    <li>
                        <strong>Extract</strong> — AI reads the full chart and extracts structured
                        clinical data across facility-specific categories (demographics, diagnosis,
                        vitals, medications, hospital course, and more).
                    </li>
                    <li>
                        <strong>Generate</strong> — a comprehensive HPI narrative and diagnosis
                        summary are generated from the extracted data. Your clinical team reviews
                        and edits the output before use.
                    </li>
                </ol>
            </>
        ),
    },
    {
        question: 'What types of facilities does CensusZen support?',
        answer: (
            <p>
                CensusZen currently supports <strong>Inpatient Rehabilitation (IPR)</strong> and{' '}
                <strong>Long-Term Acute Care (LTACH)</strong> facilities. Each facility type has
                its own set of extraction fields tailored to that clinical setting — for example,
                LTACH extractions include ventilator status, weaning progress, wound care, organ
                system assessments, and lines/tubes, while IPR extractions focus on therapy notes,
                functional status, and rehabilitation goals.
            </p>
        ),
    },
    {
        question: 'What file formats are supported?',
        answer: (
            <p>
                CensusZen accepts <strong>PDF</strong> and <strong>DOCX</strong> files. You can
                upload multiple files per case. Files are uploaded directly to encrypted cloud
                storage via time-limited signed URLs — they never pass through the application
                server.
            </p>
        ),
    },
    {
        question: 'Is CensusZen HIPAA compliant?',
        answer: (
            <>
                <p>
                    Yes. CensusZen is designed from the ground up with HIPAA compliance in mind.
                    Our entire infrastructure runs on Google Cloud's HIPAA-eligible services, and
                    we implement safeguards across every layer of the platform.
                </p>

                <h5 className="faq-category">Technical Safeguards</h5>
                <ul className="faq-detail-list">
                    <li>
                        <strong>Encryption at rest:</strong> all data in Firestore and Cloud
                        Storage is encrypted with Google-managed AES-256 keys.
                    </li>
                    <li>
                        <strong>Encryption in transit:</strong> all connections use TLS 1.2 or
                        higher. HTTPS is enforced on all services.
                    </li>
                    <li>
                        <strong>Access controls:</strong> Firebase Authentication with token
                        verification on every API request. No anonymous access to patient data.
                    </li>
                    <li>
                        <strong>Automatic session timeout:</strong> sessions expire after 15
                        minutes of inactivity with a warning prompt before logout.
                    </li>
                    <li>
                        <strong>Unique user identification:</strong> every data operation is tied
                        to an authenticated Firebase user ID.
                    </li>
                    <li>
                        <strong>Audit controls:</strong> Google Cloud Logging captures all API
                        requests. Application logs automatically redact personally identifiable
                        information (SSN, phone numbers, email, DOB, MRN patterns).
                    </li>
                </ul>

                <h5 className="faq-category">Administrative Safeguards</h5>
                <ul className="faq-detail-list">
                    <li>
                        <strong>Data isolation:</strong> Firestore security rules enforce
                        user-level data separation — each user can only access their own cases.
                        No cross-user data access is possible.
                    </li>
                    <li>
                        <strong>Role-based access:</strong> administrative functions require
                        explicit admin role verification. Standard users cannot access other
                        users' data or system-wide patient information.
                    </li>
                    <li>
                        <strong>Error monitoring:</strong> our error tracking system is configured
                        to strip all PHI — request bodies are removed, local variables are scrubbed
                        from stack traces, and personal data is never transmitted to monitoring
                        services.
                    </li>
                </ul>

                <h5 className="faq-category">Physical Safeguards</h5>
                <ul className="faq-detail-list">
                    <li>
                        All infrastructure runs on <strong>Google Cloud Platform</strong> (Cloud
                        Run, Firestore, Cloud Storage) — HIPAA-eligible services with SOC 2
                        Type II certification.
                    </li>
                    <li>
                        No on-premise servers or local data storage. Google manages all physical
                        facility security, access controls, and environmental protections.
                    </li>
                </ul>

                <h5 className="faq-category">AI / LLM Processing</h5>
                <ul className="faq-detail-list">
                    <li>
                        All AI processing runs through <strong>Google Vertex AI</strong> (Gemini),
                        a HIPAA-eligible Google Cloud service.
                    </li>
                    <li>
                        Patient documents are processed via temporary context caches that
                        automatically expire after one hour.
                    </li>
                    <li>
                        Google's terms confirm that data processed through Vertex AI is{' '}
                        <strong>not used for model training</strong>.
                    </li>
                    <li>
                        Files are referenced by secure internal URI within Google Cloud — they are
                        never transmitted over the public internet during AI processing.
                    </li>
                </ul>

                <h5 className="faq-category">Data Lifecycle</h5>
                <ul className="faq-detail-list">
                    <li>
                        <strong>Upload:</strong> files go directly to Cloud Storage via
                        time-limited signed URLs (15-minute expiry). Files never pass through
                        the application server.
                    </li>
                    <li>
                        <strong>Storage:</strong> extracted data is stored in Firestore with
                        user-level access controls. Only the authenticated owner can access
                        their cases.
                    </li>
                    <li>
                        <strong>Deletion:</strong> deleting a case permanently removes all
                        associated data from both the database and file storage. Deletion is
                        immediate and complete.
                    </li>
                    <li>
                        <strong>No data sharing:</strong> patient data is never sold, shared with
                        third parties, or used for any purpose beyond providing the service.
                    </li>
                </ul>

                <h5 className="faq-category">Business Associate Agreement</h5>
                <p>
                    CensusZen operates entirely on Google Cloud's HIPAA-eligible infrastructure.
                    A Business Associate Agreement (BAA) is available for covered entities upon
                    request. Contact us at{' '}
                    <a href="mailto:support@censuszen.com">support@censuszen.com</a> to initiate
                    the BAA process.
                </p>
            </>
        ),
    },
    {
        question: 'How does AI processing work? Is it accurate?',
        answer: (
            <>
                <p>
                    CensusZen is powered by <strong>Google Gemini</strong> via Google Cloud's
                    Vertex AI platform. The AI reads your uploaded clinical documents and extracts
                    structured data fields — patient demographics, diagnoses, medications,
                    hospital course, and more — directly from the chart text.
                </p>
                <p>
                    The AI generates a <strong>draft</strong> that your clinical team reviews and
                    edits before use. CensusZen is designed to save time on chart review, not to
                    replace clinical judgment. It is not a diagnostic tool.
                </p>
            </>
        ),
    },
    {
        question: 'Who can access my patient data?',
        answer: (
            <p>
                Only you. Firestore security rules enforce strict user-level data isolation —
                each user can only read, write, and delete their own cases. There is no
                cross-user data access. System administrators can view aggregate usage metrics
                (token counts, processing times) but cannot access case-level clinical content
                without explicit authorization.
            </p>
        ),
    },
    {
        question: 'Can I delete patient data?',
        answer: (
            <p>
                Yes. When you delete a case, all associated data is permanently removed from
                both the database (Firestore) and file storage (Google Cloud Storage). This
                includes the uploaded documents, extracted clinical data, generated narratives,
                and processing metrics. Deletion is immediate and irreversible.
            </p>
        ),
    },
    {
        question: 'How is my data secured?',
        answer: (
            <ul className="faq-detail-list">
                <li>
                    <strong>Encryption at rest</strong> — Google-managed AES-256 for all stored
                    data.
                </li>
                <li>
                    <strong>Encryption in transit</strong> — TLS 1.2+ on all connections (HTTPS
                    enforced).
                </li>
                <li>
                    <strong>Authentication</strong> — Firebase Authentication with token
                    verification on every API request.
                </li>
                <li>
                    <strong>Signed URLs</strong> — file uploads use time-limited signed URLs that
                    expire after 15 minutes.
                </li>
                <li>
                    <strong>Session management</strong> — automatic logout after 15 minutes of
                    inactivity.
                </li>
                <li>
                    <strong>Log hygiene</strong> — personally identifiable information is
                    automatically redacted from application logs.
                </li>
                <li>
                    <strong>Rate limiting</strong> — all API endpoints are rate-limited to prevent
                    abuse.
                </li>
            </ul>
        ),
    },
    {
        question: 'What happens to my data when it\'s processed by AI?',
        answer: (
            <>
                <p>
                    Your documents are processed through Google's Vertex AI (Gemini) on Google
                    Cloud infrastructure. The AI reads the document content, extracts structured
                    clinical data, and generates narrative summaries.
                </p>
                <p>
                    Importantly: your data is <strong>not used to train AI models</strong>.
                    Context caches created during processing are temporary (one-hour TTL) and
                    automatically expire. All processing stays within Google Cloud's HIPAA-eligible
                    infrastructure.
                </p>
            </>
        ),
    },
];

export const FAQContent = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const toggle = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <>
            <h3>Frequently Asked Questions</h3>
            <p>Common questions about CensusZen, data security, and HIPAA compliance.</p>

            <div className="faq-list">
                {faqs.map((faq, index) => (
                    <div key={index} className={`faq-item ${openIndex === index ? 'faq-item--open' : ''}`}>
                        <button
                            className="faq-question"
                            onClick={() => toggle(index)}
                            aria-expanded={openIndex === index}
                        >
                            <span>{faq.question}</span>
                            <svg
                                className="faq-chevron"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                            >
                                <path
                                    d="M4 6L8 10L12 6"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        {openIndex === index && (
                            <div className="faq-answer">
                                {faq.answer}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </>
    );
};
